import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptySliderDetails } from '../../../interfaces/Slider/SliderDetails';
import {
  createSlider,
  createSliderSlide,
  deleteSlider,
  deleteSliderSlide,
  getSliderDetails,
  updateSlider,
} from '../../../api/Slider';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { ImageInput } from '../../../components/ImageInput/ImageInput';
import { UpdateSliderSlideFields } from './UpdateSliderSlideFields';
import { getFeURL } from '../../../api/Settings';

export const ModifySliderPage = () => {
  const navigate = useNavigate();

  const { sliderID: sliderIDAsStr } = useParams<string>();

  const sliderID = sliderIDAsStr ? Number(sliderIDAsStr) : undefined;
  const isEditing = Number.isFinite(sliderID);

  const [sliderDetails, setSliderDetails] = useState(getEmptySliderDetails());
  const [loadingSlider, setLoadingSlider] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);
  const [feURL, setFeURL] = useState('');

  const refreshSlider = () => {
    setLoadingSlider(true);
    getSliderDetails(sliderID!)
      .then((s) => setSliderDetails(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingSlider(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshSlider();
    }

    getFeURL()
      .then((url) => setFeURL(url))
      .catch((err) => toast.error(err.message));
  }, [sliderIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoadingSlider(true);
      updateSlider(sliderDetails.slider)
        .then((slider) => {
          toast.success('Kaydırıcı başarıyla güncellendi');
          setSliderDetails({ ...sliderDetails, slider });
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoadingSlider(false));
    } else {
      setLoadingSlider(true);
      createSlider(sliderDetails.slider)
        .then((slider) => {
          toast.success('Kaydırıcı başarıyla oluşturuldu');
          navigate(`/visual/sliders/${slider.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoadingSlider(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoadingSlider(true);
    deleteSlider(sliderDetails.slider.id)
      .then(() => {
        toast.success('Kaydırıcı başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingSlider(false));
  };

  const handleDeleteSlide = (id: number) => {
    setLoadingMedia(true);
    deleteSliderSlide(id)
      .then(() => {
        toast.success('Slayt başarıyla silindi');
        refreshSlider();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingMedia(false));
  };

  const handleCreateSlide = (image?: File) => {
    if (!isEditing || !image) {
      return;
    }

    setLoadingMedia(true);
    createSliderSlide(sliderID!, image)
      .then(() => {
        toast.success('Slayt başarıyla eklendi');
        refreshSlider();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingMedia(false));
  };

  const goBack = () => navigate('/visual/sliders');

  const loading = loadingSlider || loadingMedia;

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili kaydırıcıyı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Kaydırıcıyı Düzenle' : 'Yeni Kaydırıcı'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={sliderDetails.slider.name}
          onChange={(e) =>
            setSliderDetails({
              ...sliderDetails,
              slider: { ...sliderDetails.slider, name: e.target.value },
            })
          }
          fullWidth
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            mt: 1.5,
            gap: 1.5,
          }}
        >
          {sliderDetails.slides.map((slide, i) => (
            <ImageInput
              key={i}
              title={`Slayt ${i + 1}`}
              existingImage={slide.path}
              onDelete={() => handleDeleteSlide(slide.id)}
              bottomSection={
                <UpdateSliderSlideFields
                  feURL={feURL}
                  sliderSlide={slide}
                  onUpdate={refreshSlider}
                />
              }
            />
          ))}
          <ImageInput
            key={sliderDetails.slides.length}
            title='Yeni Slayt'
            onUpload={handleCreateSlide}
          />
        </Box>
      )}
    </Box>
  );
};
